/**
 * IBSChannelStateDrawer (P18.2 / #500).
 *
 * Slide-in panel that opens on heatmap cell click. Shows the
 * current per-cell state + source attribution; admins get a state
 * picker + reason textarea + Save button. Non-admins see the cell
 * in read-only mode (useful for "what is the current state of this
 * cell" lookup).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ApiError } from "shared/api/client";
import { useMe } from "shared/api/useMe";
import type {
  HeatmapCell,
  HeatmapChannel,
  HeatmapIBS,
  LandscapePillar,
} from "shared/api/types";

import { useSetIbsChannelState } from "./useSetIbsChannelState";

const STATE_OPTIONS: { value: LandscapePillar; label: string }[] = [
  { value: "red", label: "Red" },
  { value: "amber", label: "Amber" },
  { value: "green", label: "Green" },
  { value: "unknown", label: "Unknown" },
];

const STATE_TONE: Record<LandscapePillar, string> = {
  red: "bg-rose-100 text-rose-900 border-rose-200",
  amber: "bg-amber-100 text-amber-900 border-amber-200",
  green: "bg-emerald-100 text-emerald-900 border-emerald-200",
  unknown: "bg-slate-100 text-slate-700 border-slate-200",
};

export interface IBSChannelStateDrawerProps {
  ibs: HeatmapIBS;
  channel: HeatmapChannel;
  cell: HeatmapCell;
  onClose: () => void;
}

export function IBSChannelStateDrawer({
  ibs,
  channel,
  cell,
  onClose,
}: IBSChannelStateDrawerProps) {
  const { t } = useTranslation();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  const mutation = useSetIbsChannelState();

  const [state, setState] = useState<LandscapePillar>(cell.state ?? "unknown");
  const [reason, setReason] = useState("");

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(
      {
        ibs_id: ibs.id,
        channel_id: channel.id,
        state,
        reasons: reason.trim() ? [{ message: reason.trim() }] : [],
      },
      {
        onSuccess: () => onClose(),
      },
    );
  };

  return (
    <aside
      data-testid="ibs-channel-state-drawer"
      data-mode={isAdmin ? "edit" : "read-only"}
      role="dialog"
      aria-label={t("modules.twin.channels.drawer.title")}
      className="fixed inset-y-0 end-0 w-full max-w-md bg-white border-s border-slate-200 shadow-xl overflow-y-auto z-30"
    >
      <header className="flex items-baseline justify-between p-4 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{ibs.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            <span data-testid="drawer-channel-label">
              {t("modules.twin.channels.drawer.channelLabel", {
                name: channel.name,
                code: channel.code,
              })}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="drawer-close"
          className="text-slate-500 hover:text-slate-900 px-2"
          aria-label={t("modules.twin.channels.drawer.closeLabel")}
        >
          ×
        </button>
      </header>

      <div className="p-4 space-y-4">
        <section>
          <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
            {t("modules.twin.channels.drawer.currentHeading")}
          </h3>
          <div className="flex items-center gap-2">
            <span
              data-testid="drawer-current-state"
              data-state={cell.state ?? "none"}
              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${
                cell.state
                  ? STATE_TONE[cell.state]
                  : "bg-slate-50 text-slate-500 border-slate-200"
              }`}
            >
              {cell.state ?? t("screens.iBSChannelStateDrawer.none")}
            </span>
            <span
              data-testid="drawer-state-source"
              data-source={cell.state_source}
              className="text-xs text-slate-500"
            >
              {cell.state_source === "channel"
                ? t("modules.twin.channels.drawer.sourceChannel")
                : t("modules.twin.channels.drawer.sourceIbs")}
            </span>
          </div>
        </section>

        {isAdmin ? (
          <form onSubmit={submit} className="space-y-3" data-testid="drawer-form">
            <section>
              <label
                htmlFor="ibs-channel-state-picker"
                className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1"
              >
                {t("modules.twin.channels.drawer.newStateLabel")}
              </label>
              <select
                id="ibs-channel-state-picker"
                value={state}
                onChange={(e) => setState(e.target.value as LandscapePillar)}
                data-testid="drawer-state-picker"
                className="block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              >
                {STATE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </section>
            <section>
              <label
                htmlFor="ibs-channel-reason"
                className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1"
              >
                {t("modules.twin.channels.drawer.reasonLabel")}
              </label>
              <textarea
                dir="auto"
                id="ibs-channel-reason"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="drawer-reason"
                placeholder={t("modules.twin.channels.drawer.reasonPlaceholder")}
                className="block w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </section>
            {mutation.isError && (
              <p
                data-testid="drawer-error"
                className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2"
              >
                {(mutation.error as ApiError).detail}
              </p>
            )}
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <button
                type="submit"
                disabled={mutation.isPending}
                data-testid="drawer-submit"
                className="px-3 py-1.5 rounded bg-brand-brass text-brand-navy text-sm font-medium hover:bg-brand-brassDark disabled:opacity-50"
              >
                {mutation.isPending
                  ? t("modules.twin.channels.drawer.saving")
                  : t("modules.twin.channels.drawer.save")}
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="drawer-cancel"
                className="px-3 py-1.5 rounded border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
              >
                {t("modules.twin.channels.drawer.cancel")}
              </button>
            </div>
            <p className="text-xs text-slate-400">
              {t("modules.twin.channels.drawer.auditHint")}
            </p>
          </form>
        ) : (
          <p
            data-testid="drawer-read-only-hint"
            className="text-xs italic text-slate-500"
          >
            {t("modules.twin.channels.drawer.readOnlyHint")}
          </p>
        )}
      </div>
    </aside>
  );
}
